import { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import {
  FulfillmentGroupStatus,
  HiddenStatuses,
} from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { OMSUser } from "@redotech/redo-model/fulfillments/oms-user";
import { Provider } from "@redotech/redo-model/order";
import { FulfillmentGroupFilterName } from "@redotech/redo-model/outbound-labels/fulfillment-group-summary-input";
import { WeightFormat } from "@redotech/redo-model/outbound-labels/util";
import { TagKind } from "@redotech/redo-model/tag";
import { Team } from "@redotech/redo-model/team";
import { toTitleCase } from "@redotech/redo-model/views/default-outbound-views";
import { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { AddressVerifiedFilter } from "./address-verified-filter";
import { AssignedPickerFilter } from "./assigned-picker-filter";
import { BatchIdFilter } from "./batch-id-filter";
import { BinFilter } from "./bin-filter";
import { CommercialInvoiceElectronicFilter } from "./commercial-invoice-electronic-filter";
import { CountriesFilter } from "./countries-filter";
import { CreationDateFilter } from "./create-date-filter";
import { getCustomFieldDateFilter } from "./custom-field-date-filter";
import { getCustomFieldNumberFilter } from "./custom-field-number-filter";
import { getCustomFieldStringFilter } from "./custom-field-string-filter";
import { CustomerNameFilter } from "./customer-name-filter";
import { CustomerOrderCountFilter } from "./customer-order-count-filter";
import { DeliveryMethodFilter } from "./delivery-method-filter";
import { DeliveryStatusFilter } from "./delivery-status-filter";
import { EstimatedDeliveryDateFilter } from "./estimated-delivery-date-filter";
import { ExpandedItemQuantityFilter } from "./expanded-item-quantity-filter";
import { FraudRiskFilter } from "./fraud-risk-filter";
import { FulfilledByArrayFilter } from "./fulfilled-by-filter";
import { FulfillmentOrderCountFilter } from "./fulfillment-order-count-filter";
import { ItemQuantityFilter } from "./item-quantity-filter";
import { ItemSkusFilter } from "./item-skus-filter";
import { ItemTitleFilter } from "./item-title-filter";
import { LabelPurchasedDateFilter } from "./label-purchased-date-filter";
import { MissingBinsFilter } from "./missing-bins-filter";
import { NumberOfLineItemsFilter } from "./number-of-line-items-filter";
import { OMSUserFilter } from "./oms-user-filter";
import { OrderCreatedDateFilter } from "./order-created-at-filter";
import { OrderFilter } from "./order-filter";
import { OrderNoteFilter } from "./order-note-filter";
import { OriginAddressNameFilter } from "./origin-address-name-filter";
import { OriginStoreFilter } from "./origin-store-filter";
import { PackingSlipPrintedAtFilter } from "./packing-slip-printed-at-filter";
import { PickSessionExistsFilter } from "./pick-session-exists-filter";
import { PickSessionIdFilter } from "./pick-session-id-filter";
import { PickStartedOnFilter } from "./pick-started-on-filter";
import { PickStatusArrayFilter } from "./pick-status-filter";
import { PickedAtFilter } from "./picked-at-filter";
import { PrinterFilter } from "./printer-filter";
import { ProductTagsFilter } from "./product-tags-filter";
import { ProductVariantFilter } from "./product-variant-filter";
import { RequestedServiceFilter } from "./requested-service-filter";
import { SalesChannelFilter } from "./sales-channel-filter";
import { ScanVerifiedFilter } from "./scan-verified-filter";
import { SelectedCarrierFilter } from "./selected-carrier-filter";
import { SelectedParcelsNameFilter } from "./selected-parcels-name-filter";
import { ShippedDateFilter } from "./shipped-date-filter";
import { ShopifyTagFilter } from "./shopify-tag-filter";
import { SLADeliveryPromiseFilter } from "./sla-filter";
import { OrderSourceFilter } from "./source-filter";
import { StateFilter } from "./state-filter";
import { StatusArrayFilter } from "./status-filter";
import { TagFilter } from "./tag-filter";
import { TotalPriceFilter } from "./total-price-filter";
import { UsedParcelLearningFilter } from "./used-parcel-learning-filter";
import { UsedWeightLearningFilter } from "./used-weight-learning-filter";
import { VendorsFilter } from "./vendors-filter";
import { VoidedBatchIdFilter } from "./voided-batch-id-filter";
import { WeightFilter } from "./weight-filter";

export const getFGAdvancedFilters = ({
  team,
  users,
  rpcClient,
  rpcMerchantClient,
  enabledProviders,
}: {
  team: Team;
  users: OMSUser[];
  rpcClient: RedoOutboundLabelsRpcClient;
  rpcMerchantClient: RedoMerchantRpcClient;
  enabledProviders: readonly Provider[];
}): AdvancedTableFilter[] => {
  const wmsEnabled = team?.settings?.outboundLabels?.wms?.enabled ?? false;
  const advancedFilters: AdvancedTableFilter[] = [
    CreationDateFilter,
    OrderSourceFilter,
    StatusArrayFilter(createFulfillmentGroupStatusFetcher(enabledProviders)),
    OrderCreatedDateFilter,
    ItemQuantityFilter,
    ExpandedItemQuantityFilter,
    TotalPriceFilter,
    WeightFilter(
      team?.settings?.outboundLabels?.weightFormat ?? WeightFormat.IMPERIAL,
    ),
    NumberOfLineItemsFilter,
    DeliveryMethodFilter,
    SelectedCarrierFilter,
    OrderNoteFilter,
    ShippedDateFilter,
    LabelPurchasedDateFilter,
    SLADeliveryPromiseFilter,
    ItemSkusFilter(createValueFetcher("itemSkus", rpcClient)),
    CountriesFilter(createValueFetcher("countries", rpcClient)),
    RequestedServiceFilter(createValueFetcher("requestedService", rpcClient)),
    OMSUserFilter(users),
    ItemTitleFilter(createValueFetcher("itemTitle", rpcClient)),
    SalesChannelFilter(createValueFetcher("stores", rpcClient)),
    OriginStoreFilter(createValueFetcher("originStore", rpcClient)),
    FraudRiskFilter(),
    BatchIdFilter(createValueFetcher("batchId", rpcClient)),
    VoidedBatchIdFilter(createValueFetcher("voidedBatchId", rpcClient)),
    ProductVariantFilter(createValueFetcher("productVariant", rpcClient)),
    VendorsFilter(createValueFetcher("vendors", rpcClient)),
    OriginAddressNameFilter(createValueFetcher("originAddressName", rpcClient)),
    CustomerOrderCountFilter,
    CustomerNameFilter(createValueFetcher("customer", rpcClient)),
    TagFilter(
      async () =>
        (
          await rpcMerchantClient.getTeamTags({
            kind: TagKind.FULFILLMENT_GROUP,
          })
        ).tags,
    ),
    ShopifyTagFilter(createValueFetcher("shopifyOrderTags", rpcClient)),
    ProductTagsFilter(createValueFetcher("productTags", rpcClient)),
    OrderFilter(createValueFetcher("order", rpcClient)),
    PrinterFilter("labelPrinted", "Label", "Label printed"),
    PrinterFilter("pickListPrinted", "Pick list", "Pick list printed"),
    PrinterFilter("packingSlipPrinted", "Packing slip", "Packing slip printed"),
    ScanVerifiedFilter,
    FulfilledByArrayFilter,
    CommercialInvoiceElectronicFilter,
    AddressVerifiedFilter,
    BinFilter(createValueFetcher("bin", rpcClient)),
    DeliveryStatusFilter,
    EstimatedDeliveryDateFilter,
    StateFilter,
    PackingSlipPrintedAtFilter,
    FulfillmentOrderCountFilter,
    MissingBinsFilter,
    UsedWeightLearningFilter,
    UsedParcelLearningFilter,
    SelectedParcelsNameFilter(
      createValueFetcher("selectedParcelsName", rpcClient),
    ),
    PickSessionExistsFilter,
    PickSessionIdFilter,
    ...(wmsEnabled
      ? [
          PickStatusArrayFilter(),
          PickedAtFilter,
          PickStartedOnFilter,
          AssignedPickerFilter(users),
        ]
      : []),
  ];

  const customAttributes = team?.settings?.outboundLabels?.customAttributes;

  if (customAttributes?.customAttributeDate1) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate1",
        customAttributes?.customAttributeDate1?.displayName ||
          customAttributes?.customAttributeDate1?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeDate2) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate2",
        customAttributes?.customAttributeDate2?.displayName ||
          customAttributes?.customAttributeDate2?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeDate3) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customAttributeDate3",
        customAttributes?.customAttributeDate3?.displayName ||
          customAttributes?.customAttributeDate3?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber1) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber1",
        customAttributes?.customAttributeNumber1?.displayName ||
          customAttributes?.customAttributeNumber1?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber2) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber2",
        customAttributes?.customAttributeNumber2?.displayName ||
          customAttributes?.customAttributeNumber2?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeNumber3) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customAttributeNumber3",
        customAttributes?.customAttributeNumber3?.displayName ||
          customAttributes?.customAttributeNumber3?.key,
      ),
    );
  }

  if (customAttributes?.customAttributeString1) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString1",
        customAttributes?.customAttributeString1?.displayName ||
          customAttributes?.customAttributeString1?.key,
        createValueFetcher("customAttributeString1", rpcClient),
      ),
    );
  }

  if (customAttributes?.customAttributeString2) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString2",
        customAttributes?.customAttributeString2?.displayName ||
          customAttributes?.customAttributeString2?.key,
        createValueFetcher("customAttributeString2", rpcClient),
      ),
    );
  }

  if (customAttributes?.customAttributeString3) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customAttributeString3",
        customAttributes?.customAttributeString3?.displayName ||
          customAttributes?.customAttributeString3?.key,
        createValueFetcher("customAttributeString3", rpcClient),
      ),
    );
  }

  const customMetadata = team?.settings?.outboundLabels?.customMetadata;

  if (customMetadata?.customDate1) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate1",
        customMetadata?.customDate1?.displayName ||
          customMetadata?.customDate1?.key,
      ),
    );
  }

  if (customMetadata?.customDate2) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate2",
        customMetadata?.customDate2?.displayName ||
          customMetadata?.customDate2?.key,
      ),
    );
  }

  if (customMetadata?.customDate3) {
    advancedFilters.push(
      getCustomFieldDateFilter(
        "customDate3",
        customMetadata?.customDate3?.displayName ||
          customMetadata?.customDate3?.key,
      ),
    );
  }

  if (customMetadata?.customNumber1) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber1",
        customMetadata?.customNumber1?.displayName ||
          customMetadata?.customNumber1?.key,
      ),
    );
  }

  if (customMetadata?.customNumber2) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber2",
        customMetadata?.customNumber2?.displayName ||
          customMetadata?.customNumber2?.key,
      ),
    );
  }

  if (customMetadata?.customNumber3) {
    advancedFilters.push(
      getCustomFieldNumberFilter(
        "customNumber3",
        customMetadata?.customNumber3?.displayName ||
          customMetadata?.customNumber3?.key,
      ),
    );
  }

  if (customMetadata?.customString1) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString1",
        customMetadata?.customString1?.displayName ||
          customMetadata?.customString1?.key,
        createValueFetcher("customString1", rpcClient),
      ),
    );
  }

  if (customMetadata?.customString2) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString2",
        customMetadata?.customString2?.displayName ||
          customMetadata?.customString2?.key,
        createValueFetcher("customString2", rpcClient),
      ),
    );
  }

  if (customMetadata?.customString3) {
    advancedFilters.push(
      getCustomFieldStringFilter(
        "customString3",
        customMetadata?.customString3?.displayName ||
          customMetadata?.customString3?.key,
        createValueFetcher("customString3", rpcClient),
      ),
    );
  }

  return advancedFilters.sort((a, b) => {
    return a.data.name.localeCompare(b.data.name);
  });
};

export function createValueFetcher(
  filterName: string,
  rpcClient: RedoOutboundLabelsRpcClient,
): (
  {
    filters,
    filterSearch,
    applyTableState,
  }: {
    filters: AdvancedTableFilter[];
    filterSearch?: string;
    applyTableState?: boolean;
  },
  signal: AbortSignal,
) => Promise<string[]> {
  return async ({ filters, filterSearch, applyTableState }, signal) => {
    const { values } = await rpcClient.getFulfillmentGroupUniqueValues(
      {
        advancedFiltersData: applyTableState
          ? filters.map((filter) => filter.data)
          : [],
        filterName: filterName as FulfillmentGroupFilterName,
        filterSearch,
      },
      { signal },
    );
    return values;
  };
}

// Custom fetcher for the status values to hide the OpenNotReceived status if the merchant does not have a CS integration
function createFulfillmentGroupStatusFetcher(
  enabledProviders: readonly Provider[],
): (
  {
    filters,
    filterSearch,
    applyTableState,
  }: {
    filters: AdvancedTableFilter[];
    filterSearch?: string;
    applyTableState?: boolean;
  },
  signal: AbortSignal,
) => Promise<string[]> {
  return async ({ filters, filterSearch, applyTableState }, signal) => {
    const isCommentsoldEnabled = enabledProviders.includes(
      Provider.COMMENTSOLD,
    );
    return Object.values(FulfillmentGroupStatus)
      .filter((status) => {
        if (
          status === FulfillmentGroupStatus.OpenNotReceived &&
          !isCommentsoldEnabled
        ) {
          return false;
        }

        return !HiddenStatuses.includes(status);
      })
      .map((status) => toTitleCase(status));
  };
}
